import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ChevronLeft, Save, Loader2, Trash2, Upload, GripVertical, Star, Sparkles, Image as ImageIcon
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { RichEditor } from "@/components/RichEditor";
import { API, R2_URL } from "@/lib/api";

export default function AdminProductEdit() {
  const { productId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const isNew = productId === 'novy';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState('');
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const [form, setForm] = useState({
    name: '', description: '', description_short: '', price: 0, price_vat: 0,
    quantity: 0, reference: '', ean13: '', weight: 0, active: 1, on_sale: 0,
    id_category_default: null, meta_title: '', meta_description: '', meta_keywords: ''
  });

  useEffect(() => {
    if (!isNew) {
      Promise.all([
        axios.get(`${API}/products/${productId}`),
        axios.get(`${API}/products/${productId}/images`).catch(() => ({ data: { images: [] } })),
      ]).then(([prodRes, imgRes]) => {
        setForm(prodRes.data);
        setImages(imgRes.data.images || []);
      }).catch(() => toast.error('Produkt nenalezen'))
        .finally(() => setLoading(false));
    }
  }, [productId, isNew]);

  const up = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name, description: form.description, description_short: form.description_short,
        price: form.price, price_vat: form.price_vat, quantity: form.quantity,
        reference: form.reference, ean13: form.ean13, weight: form.weight,
        active: form.active, on_sale: form.on_sale, id_category_default: form.id_category_default,
        meta_title: form.meta_title, meta_description: form.meta_description, meta_keywords: form.meta_keywords,
      };
      if (isNew) {
        const res = await axios.post(`${API}/admin/products`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Produkt vytvoren');
        navigate(`/admin/produkty/${res.data.id_product}`);
      } else {
        await axios.put(`${API}/admin/products/${productId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Produkt ulozen');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Chyba ukladani'); }
    finally { setSaving(false); }
  };

  const uploadFile = useCallback(async (file) => {
    if (!productId || isNew) { toast.error('Nejdriv ulozte produkt'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/products/${productId}/images`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setImages(prev => [...prev, res.data.image]);
      toast.success('Obrazek nahran');
    } catch (err) { toast.error(err.response?.data?.detail || 'Chyba nahravani'); }
    finally { setUploading(false); }
  }, [productId, isNew, token]);

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files) Array.from(files).forEach(f => uploadFile(f));
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files) Array.from(files).forEach(f => uploadFile(f));
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Smazat obrazek?')) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}/images/${imageId}`, { headers: { Authorization: `Bearer ${token}` } });
      setImages(prev => prev.filter(i => i.id_image !== imageId));
      toast.success('Obrazek smazan');
    } catch { toast.error('Chyba mazani'); }
  };

  const setCover = async (imageId) => {
    try {
      await axios.put(`${API}/admin/products/${productId}/images/${imageId}/cover`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setImages(prev => prev.map(i => ({ ...i, cover: i.id_image === imageId })));
      toast.success('Hlavni obrazek nastaven');
    } catch { toast.error('Chyba'); }
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOverImg = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const newImages = [...images];
    const [moved] = newImages.splice(dragIdx, 1);
    newImages.splice(idx, 0, moved);
    setImages(newImages);
    setDragIdx(idx);
  };
  const handleDragEnd = async () => {
    setDragIdx(null);
    try {
      await axios.put(`${API}/admin/products/${productId}/images/reorder`,
        { image_ids: images.map(i => i.id_image) },
        { headers: { Authorization: `Bearer ${token}` } });
    } catch { toast.error('Chyba razeni'); }
  };

  const generateAI = async (type) => {
    setAiLoading(type);
    try {
      const res = await axios.post(`${API}/admin/products/ai-generate`, {
        product_name: form.name,
        current_description: form.description,
        current_short_description: form.description_short,
        generate_type: type,
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.error) { toast.error(res.data.error); return; }
      if (res.data.description) up('description', res.data.description);
      if (res.data.short_description) up('description_short', res.data.short_description);
      if (res.data.meta_title) up('meta_title', res.data.meta_title);
      if (res.data.meta_description) up('meta_description', res.data.meta_description);
      if (res.data.meta_keywords) up('meta_keywords', res.data.meta_keywords);
      toast.success('AI text vygenerovan');
    } catch (err) { toast.error(err.response?.data?.detail || 'AI generovani selhalo'); }
    finally { setAiLoading(''); }
  };

  if (loading) return <AdminLayout active="products"><div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#004E98]" /></div></AdminLayout>;

  const inputCls = "w-full h-11 border border-slate-300 rounded px-4 text-sm focus:border-[#004E98] focus:ring-1 focus:ring-[#004E98] outline-none";
  const labelCls = "text-xs font-bold uppercase text-slate-500 mb-1 block";

  return (
    <AdminLayout active="products">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/admin/produkty')} className="text-slate-500 hover:text-[#004E98]" data-testid="back-to-products"><ChevronLeft className="w-6 h-6" /></button>
        <h1 className="text-2xl font-bold text-slate-800" data-testid="product-edit-title">{isNew ? 'Novy produkt' : `Upravit: ${form.name}`}</h1>
      </div>

      <form onSubmit={handleSave} className="max-w-5xl space-y-6">
        {/* Basic info */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-semibold text-slate-800 mb-4">Zakladni informace</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><label className={labelCls}>Nazev *</label>
              <input type="text" required value={form.name} onChange={e => up('name', e.target.value)} className={inputCls} data-testid="product-name-input" /></div>
            <div><label className={labelCls}>Cena bez DPH (Kc)</label>
              <input type="number" step="0.01" value={form.price} onChange={e => up('price', parseFloat(e.target.value) || 0)} className={inputCls} data-testid="product-price-input" /></div>
            <div><label className={labelCls}>Cena s DPH (Kc)</label>
              <input type="number" step="0.01" value={form.price_vat || Math.round(form.price * 1.21 * 100) / 100} onChange={e => up('price_vat', parseFloat(e.target.value) || 0)} className={inputCls} /></div>
            <div><label className={labelCls}>Skladem (ks)</label>
              <input type="number" value={form.quantity} onChange={e => up('quantity', parseInt(e.target.value) || 0)} className={inputCls} data-testid="product-quantity-input" /></div>
            <div><label className={labelCls}>Kod (reference)</label>
              <input type="text" value={form.reference || ''} onChange={e => up('reference', e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>EAN</label>
              <input type="text" value={form.ean13 || ''} onChange={e => up('ean13', e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Hmotnost (kg)</label>
              <input type="number" step="0.01" value={form.weight || 0} onChange={e => up('weight', parseFloat(e.target.value) || 0)} className={inputCls} /></div>
            <div className="flex items-center gap-6 pt-6">
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.active === 1} onChange={e => up('active', e.target.checked ? 1 : 0)} className="w-4 h-4 text-[#004E98] rounded" /><span className="text-sm font-medium">Aktivni</span></label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.on_sale === 1} onChange={e => up('on_sale', e.target.checked ? 1 : 0)} className="w-4 h-4 text-[#FF6B35] rounded" /><span className="text-sm font-medium">Akce</span></label>
            </div>
          </div>
        </div>

        {/* Images */}
        {!isNew && (
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2"><ImageIcon className="w-5 h-5" /> Obrazky produktu</h2>
              <span className="text-sm text-slate-400">{images.length} obrazku</span>
            </div>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors mb-4 ${dragOver ? 'border-[#004E98] bg-blue-50' : 'border-slate-300 hover:border-slate-400'}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              data-testid="image-drop-zone"
            >
              {uploading ? (
                <div className="flex items-center justify-center gap-2 text-[#004E98]"><Loader2 className="w-6 h-6 animate-spin" /> Nahravani a optimalizace...</div>
              ) : (
                <div>
                  <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Pretahnete obrazky sem nebo</p>
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="text-sm text-[#004E98] font-medium hover:underline" data-testid="image-upload-btn">vyberte ze souboru</button>
                  <p className="text-xs text-slate-400 mt-1">PNG, JPEG, SVG, WebP - automaticky konvertovano na WebP a optimalizovano</p>
                  <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handleFileSelect} className="hidden" />
                </div>
              )}
            </div>
            {images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {images.map((img, idx) => (
                  <div key={img.id_image} draggable onDragStart={() => handleDragStart(idx)} onDragOver={(e) => handleDragOverImg(e, idx)} onDragEnd={handleDragEnd}
                    className={`relative group border-2 rounded-lg overflow-hidden aspect-square bg-slate-50 cursor-grab ${img.cover ? 'border-[#004E98] ring-2 ring-[#004E98]/20' : 'border-slate-200'} ${dragIdx === idx ? 'opacity-50 scale-95' : ''} transition-all`}
                    data-testid={`product-image-${img.id_image}`}>
                    <img src={`${R2_URL}/p/${img.sizes?.medium || img.path}`} alt={img.legend || ''} className="w-full h-full object-contain p-1" loading="lazy" />
                    {img.cover && <div className="absolute top-1 left-1 bg-[#004E98] text-white text-[10px] px-1.5 py-0.5 rounded font-medium">Hlavni</div>}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => setCover(img.id_image)} className="p-1.5 bg-white rounded shadow hover:bg-blue-50" title="Nastavit jako hlavni"><Star className="w-4 h-4 text-[#004E98]" /></button>
                        <button type="button" className="p-1.5 bg-white rounded shadow cursor-grab" title="Pretahnout"><GripVertical className="w-4 h-4 text-slate-500" /></button>
                        <button type="button" onClick={() => deleteImage(img.id_image)} className="p-1.5 bg-white rounded shadow hover:bg-red-50" title="Smazat"><Trash2 className="w-4 h-4 text-red-500" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Descriptions with WYSIWYG */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-800">Popis produktu</h2>
            {form.name && (
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => generateAI('short_description')} disabled={!!aiLoading}
                  className="h-8 px-3 bg-purple-50 text-purple-700 border border-purple-200 rounded text-xs font-medium flex items-center gap-1 hover:bg-purple-100 disabled:opacity-50" data-testid="ai-short-desc-btn">
                  {aiLoading === 'short_description' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI kratky
                </button>
                <button type="button" onClick={() => generateAI('description')} disabled={!!aiLoading}
                  className="h-8 px-3 bg-purple-50 text-purple-700 border border-purple-200 rounded text-xs font-medium flex items-center gap-1 hover:bg-purple-100 disabled:opacity-50" data-testid="ai-full-desc-btn">
                  {aiLoading === 'description' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI plny
                </button>
                <button type="button" onClick={() => generateAI('all')} disabled={!!aiLoading}
                  className="h-8 px-3 bg-purple-600 text-white rounded text-xs font-medium flex items-center gap-1 hover:bg-purple-700 disabled:opacity-50" data-testid="ai-all-btn">
                  {aiLoading === 'all' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI vse
                </button>
              </div>
            )}
          </div>
          <div className="space-y-5">
            <div>
              <label className={labelCls}>Kratky popis</label>
              <RichEditor value={form.description_short || ''} onChange={val => up('description_short', val)} minHeight="80px" />
            </div>
            <div>
              <label className={labelCls}>Plny popis</label>
              <RichEditor value={form.description || ''} onChange={val => up('description', val)} minHeight="200px" />
            </div>
          </div>
        </div>

        {/* SEO Metadata */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-800">SEO metadata</h2>
            {form.name && (
              <button type="button" onClick={() => generateAI('seo')} disabled={!!aiLoading}
                className="h-8 px-3 bg-purple-50 text-purple-700 border border-purple-200 rounded text-xs font-medium flex items-center gap-1 hover:bg-purple-100 disabled:opacity-50" data-testid="ai-seo-btn">
                {aiLoading === 'seo' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI SEO
              </button>
            )}
          </div>
          <div className="space-y-4">
            <div><label className={labelCls}>Meta Title <span className="text-slate-400 font-normal">(max 60 znaku)</span></label>
              <input type="text" value={form.meta_title || ''} onChange={e => up('meta_title', e.target.value)} maxLength={70} className={inputCls} data-testid="meta-title-input" />
              <div className="text-xs text-slate-400 mt-1">{(form.meta_title || '').length}/60</div>
            </div>
            <div><label className={labelCls}>Meta Description <span className="text-slate-400 font-normal">(max 160 znaku)</span></label>
              <textarea value={form.meta_description || ''} onChange={e => up('meta_description', e.target.value)} maxLength={170} rows={2} className="w-full border border-slate-300 rounded p-3 text-sm focus:border-[#004E98] focus:ring-1 focus:ring-[#004E98] outline-none" data-testid="meta-description-input" />
              <div className="text-xs text-slate-400 mt-1">{(form.meta_description || '').length}/160</div>
            </div>
            <div><label className={labelCls}>Klicova slova <span className="text-slate-400 font-normal">(oddelena carkou)</span></label>
              <input type="text" value={form.meta_keywords || ''} onChange={e => up('meta_keywords', e.target.value)} className={inputCls} placeholder="klicove slovo 1, klicove slovo 2, ..." data-testid="meta-keywords-input" /></div>
          </div>
          {(form.meta_title || form.name) && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-400 uppercase font-bold mb-2">Nahled ve vyhledavaci</p>
              <div className="text-[#1a0dab] text-lg font-medium truncate">{form.meta_title || form.name}</div>
              <div className="text-[#006621] text-sm truncate">example.com/produkt/{productId}</div>
              <div className="text-sm text-slate-600 line-clamp-2">{form.meta_description || form.description_short?.replace(/<[^>]*>/g, '')?.slice(0, 160) || ''}</div>
            </div>
          )}
        </div>

        {/* Save */}
        <div className="flex items-center gap-3 pb-8">
          <button type="submit" disabled={saving} className="h-11 px-6 bg-[#004E98] text-white rounded font-medium flex items-center gap-2 hover:bg-[#003B73] disabled:opacity-50" data-testid="save-product-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {isNew ? 'Vytvorit produkt' : 'Ulozit zmeny'}
          </button>
          <button type="button" onClick={() => navigate('/admin/produkty')} className="h-11 px-6 border border-slate-300 rounded text-sm hover:bg-slate-50">Zrusit</button>
        </div>
      </form>
    </AdminLayout>
  );
}
